'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import WhatsAppContactButton from './WhatsAppContactButton';

interface HeaderProps {
  className?: string;
}

export default function Header({ className = '' }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();

  const navigationItems = [
    { label: 'Home', href: '/homepage' },
    { label: 'Services', href: '/services' },
    { label: 'About Us', href: '/about-us' },
    { label: 'Contact', href: '/contact' }
  ];

  const isActive = (href: string) => {
    if (href === '/homepage') {
      return pathname === '/' || pathname === '/homepage';
    }
    return pathname.startsWith(href);
  };

  return (
    <header className={`bg-background/95 backdrop-blur-md border-b border-border sticky top-0 z-50 shadow-sm ${className}`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-20 px-4 md:px-6 lg:px-8">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center space-x-3 group">
            <div className="relative w-12 h-12 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary-dark rounded-full opacity-10 group-hover:opacity-20 transition-opacity" />
              <AppImage 
                src="/assets/images/os_logo_banner.jpg" 
                alt="OS Property Services"
                width={48}
                height={48}
                className="w-full h-full object-contain relative z-10"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-heading text-xl font-bold text-text-primary leading-tight tracking-tight group-hover:text-primary transition-colors">
                OS Property Services
              </span>
              <span className="text-xs font-medium text-text-tertiary leading-tight whitespace-nowrap">
                Property Maintenance
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  isActive(item.href)
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-text-secondary hover:text-primary hover:bg-primary/5'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons - Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            <WhatsAppContactButton 
              variant="default"
              size="sm"
              showLabel={true}
            />
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:bg-primary-dark transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <Icon name="DocumentTextIcon" size={18} variant="solid" />
              <span>Request a Quote</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-surface transition-colors"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              variant="outline"
              className="text-text-primary"
            />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-border bg-background animate-slide-up">
            <nav className="flex flex-col px-4 py-4 gap-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                    isActive(item.href)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-text-secondary hover:text-primary hover:bg-primary/5'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-border">
                <WhatsAppContactButton 
                  variant="inline"
                  size="md"
                  showLabel={true}
                  className="w-full justify-center"
                />
                <Link
                  href="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:bg-primary-dark transition-all duration-200"
                >
                  <Icon name="DocumentTextIcon" size={18} variant="solid" />
                  <span>Get Quote</span>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
